import { StatusChip } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  IAdminDisclosureSummary,
  IAdminParticipantHub
} from '../../api/participant-contracts';

/**
 * One member's disclosures at a glance (SR-216's R3) — the hub's `disclosures` entry for it, in
 * the one rendering the hub draws in three places (its Q2): the opened participant's in the
 * header, each member's on its Overview card, and each member's at the head of its part of the
 * Disclosures section.
 *
 * **Counts and stamps, never a verdict.** The counts are survey-api's, drawn as sent ("2 live of
 * 3", or "None on file" at a total of 0) — never derived from a list, which may say otherwise
 * — and the check is its newest stamp, or "never checked". "Clean" is said nowhere: it is the
 * People filter's value, not a reading of a person. The tone is `attention` while anything is
 * live and `neutral` otherwise — never `success`, which would be the verdict this declines, nor
 * `danger`, which would be a judgement of what a disclosure means.
 *
 * **A null summary is "No CRD to check disclosures for", never "no CRD held".** It means the
 * member's CRD set is empty: no number survey-api reads as a CRD (1–10 digits), and no migrated
 * disclosure filed under its records. Identity & numbers can still show a `CRD` identifier that
 * is not one under that rule — the ETL parked nine such.
 */
export function DisclosureSummary({ summary }: { summary: Nullable<IAdminDisclosureSummary> }) {
  return (
    <span data-disclosure-summary className="inline-flex max-w-full">
      <StatusChip tone={summary && summary.liveCount > 0 ? 'attention' : 'neutral'} wrap>
        {summary ? `Disclosures: ${counts(summary)} · ${check(summary)}` : NO_CRD}
      </StatusChip>
    </span>
  );
}

const NO_CRD = 'No CRD to check disclosures for';

function counts(summary: IAdminDisclosureSummary): string {
  return summary.totalCount === 0
    ? 'None on file'
    : `${summary.liveCount} live of ${summary.totalCount}`;
}

function check(summary: IAdminDisclosureSummary): string {
  if (!summary.checked) return 'never checked';
  return summary.refreshedAt ? `checked ${formatDateTime(summary.refreshedAt)}` : 'checked';
}

/**
 * A member's summary as the hub sent it — `undefined` where the document carries no entry for
 * that member, which survey-api never does, and which then draws nothing rather than claiming
 * the member has no CRD.
 */
export function disclosureSummaryOf(
  hub: IAdminParticipantHub,
  participantId: string
): Nullable<IAdminDisclosureSummary> | undefined {
  return hub.disclosures.find((entry) => entry.participantId === participantId)?.summary;
}
